<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="950px">
      <v-skeleton-loader v-if="loading" type="article, actions"></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.legal-entities.edit-window.new_title")
                  : $t("wkt.admin.legal-entities.edit-window.edit_title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-form ref="form">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.legalEntityName"
                    :label="$t('wkt.admin.legal-entities.edit-window.name')"
                    autocomplete="none"
                    :rules="[validationRules.required]"
                    max="100"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.legalEntityCode"
                    :label="$t('wkt.admin.legal-entities.edit-window.code')"
                    autocomplete="none"
                    max="50"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="data.countryId"
                    :items="countries"
                    :item-text="item => $t(item.name)"
                    item-value="id"
                    menu-props="bottom, offsetY"
                    :label="$t('wkt.admin.workplace.edit-window.country')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.taxId"
                    :label="$t('wkt.admin.gconfig.cif')"
                    autocomplete="none"
                    :rules="data.taxId ? [validationRules.cifnif(data.taxId, selectedCountry)] : []"
                    max="50"
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="data.address"
                    :label="$t('wkt.admin.gconfig.address')"
                    autocomplete="none"
                    max="150"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="data.postalCode"
                    :label="$t('wkt.admin.gconfig.postal_code')"
                    autocomplete="none"
                    max="10"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.location"
                    :label="$t('wkt.admin.gconfig.location')"
                    autocomplete="none"
                    max="45"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="data.contactInfo"
                    :label="$t('wkt.admin.legal-entities.edit-window.contact_info')"
                    autocomplete="none"
                    max="250"
                  />
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="data.description"
                    :label="$t('wkt.admin.legal-entities.edit-window.description')"
                    auto-grow
                    rows="3"
                    max="400"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{ $t("wkt.admin.common.dialog-buttons.delete").toUpperCase() }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#185EA9" text @click="onSave()" style="font-size: 0.9em; font-weight: 400;">
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn text @click="showDialog = false" style="font-size: 0.9em; font-weight: 400;">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      :title="$t('wkt.admin.legal-entities.confirm-dialog.title')"
      :message="$t('wkt.admin.legal-entities.confirm-dialog.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";

export default {
  name: "LegalEntityDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      data: {},
      countries: []
    };
  },
  computed: {
    selectedCountry() {
      if (!this.countries || !this.countries.length) {
        return null;
      }
      return this.countries.find((country) => country.id === this.data.countryId) || null;
    }
  },
  methods: {
    getErrorMessage(error) {
      if (!error) {
        return this.$t("wkt.admin.common.table.no_results");
      }

      if (typeof error === "string") {
        return error;
      }

      if (error.message && typeof error.message === "string") {
        return error.message;
      }

      if (error.response && error.response.data) {
        if (typeof error.response.data === "string") {
          return error.response.data;
        }

        if (error.response.data.message) {
          return error.response.data.message;
        }
      }

      return "Error";
    },
    open(action, payload) {
      this.showDialog = true;
      this.countries = gwt.getSessionInformation()?.countries || [];

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      if (!payload) {
        this.data = {
          companyId: gwt.getSessionProfile().companyId,
          countryId: gwt.getSessionInformation()?.company?.countryId || null
        };
        this.loading = false;
        return;
      }

      this.loading = true;
      api.getLegalEntityById(
        payload,
        (res) => {
          this.data = res;
          this.loading = false;
        },
        (error) => {
          this.$emit("show-alert", { text: this.getErrorMessage(error), color: "error", timeout: 3000 });
          this.loading = false;
        }
      );
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      api.storeLegalEntity(
        this.data,
        () => {
          this.loading = false;
          this.$emit("refresh");
          this.showDialog = false;
        },
        (error) => {
          this.$log.error(error);
          this.loading = false;
          this.$emit("show-alert", { text: this.getErrorMessage(error), color: "error", timeout: 3000 });
        }
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;
      api.deleteLegalEntity(
        this.data.id,
        () => {
          this.showDialog = false;
          this.loading = false;
          this.$emit("refresh");
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", { text: this.getErrorMessage(error), color: "error", timeout: 4000 });
        }
      );
    }
  }
};
</script>
